import { Anchor, Badge, Button, Group, Select, Table, Text } from '@mantine/core';
import { IconAlertTriangle, IconListDetails, IconPlus } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { api, podatki } from '../api/odjemalec';
import { datum } from '../oblika/oblika';
import { usePravica } from '../seja/pravice';
import { GlavaZaslona, IskalnoPolje, Seznam, Stolpec, ZaslonSeznama } from '../seznam/Seznam';
import { useSeznam } from '../seznam/useSeznam';
import { opisGum, profil, SEZONE, STANJA_GUM, type SezonaGum } from './gume';

type Razvrsti = 'lokacija' | 'kupec' | 'vozilo' | 'sezona' | 'dimenzija' | 'profil' | 'shranjeno';

const IZBIRA_STANJA = [
  { value: 'SHRANJEN', label: 'V hotelu' },
  { value: 'PREVZET', label: 'Prevzete' },
  { value: 'VSI', label: 'Vse' },
];
const IZBIRA_SEZONE = (Object.keys(SEZONE) as SezonaGum[]).map((s) => ({ value: s, label: SEZONE[s].naziv }));

/**
 * Hotel za gume (modul Delavnica): kompleti gum strank v skladišču, privzeto tisti v hotelu. Iskanje po stranki,
 * registrski, lokaciji, dimenziji ali znamki, filtra stanje in sezona, razvrščanje in strani na strežniku. Klik na
 * vrstico odpre komplet, »Shrani gume« nov komplet. Stolpec Račun: številka izdanega računa ali »Ni izdan« (postavka
 * izbrana, račun še ne).
 */
export function PregledGum() {
  const ureja = usePravica('DELAVNICA', 'UREJANJE');
  const seznam = useSeznam<Razvrsti>();
  const navigate = useNavigate();
  const stanje = seznam.parameter('stanje') ?? 'SHRANJEN';
  const sezona = seznam.parameter('sezona') as SezonaGum | null;
  const poizvedba = useQuery({
    queryKey: ['gume', seznam.iskanje, stanje, sezona, seznam.zaApi],
    queryFn: ({ signal }) =>
      podatki(
        api.GET('/api/delavnica/gume/pregled', {
          signal,
          params: {
            query: {
              iskanje: seznam.iskanje || undefined,
              stanje: stanje === 'SHRANJEN' ? undefined : stanje,
              sezona: sezona ?? undefined,
              ...seznam.zaApi,
            },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const vrstice = poizvedba.data?.vsebina ?? [];

  return (
    <ZaslonSeznama>
      <GlavaZaslona
        naslov="Hotel za gume"
        opis="Gume strank v vašem skladišču: kje so, čigave so in v kakšnem stanju."
        pomoc="hotel-za-gume"
        dejanja={
          <Group gap="xs">
            <Button
              component={Link}
              to="/delavnica/gume/cenik"
              variant="default"
              leftSection={<IconListDetails size={18} />}
            >
              Cenik
            </Button>
            {ureja && (
              <Button component={Link} to="/delavnica/gume/nov" leftSection={<IconPlus size={18} />}>
                Shrani gume
              </Button>
            )}
          </Group>
        }
      />
      <Seznam
        seznam={seznam}
        filtri={
          <>
            <IskalnoPolje seznam={seznam} namig="Išči po stranki, registrski, lokaciji, dimenziji ali znamki" />
            <Select
              label="Stanje"
              data={IZBIRA_STANJA}
              value={stanje}
              onChange={(v) => seznam.nastavi({ stanje: !v || v === 'SHRANJEN' ? null : v })}
              allowDeselect={false}
              w={{ base: '100%', sm: 160 }}
            />
            <Select
              label="Sezona"
              placeholder="Vse"
              data={IZBIRA_SEZONE}
              value={sezona}
              onChange={(v) => seznam.nastavi({ sezona: v })}
              clearable
              w={{ base: '100%', sm: 160 }}
            />
          </>
        }
        glava={
          <>
            <Stolpec seznam={seznam} kljuc="lokacija" naziv="Lokacija" />
            <Stolpec seznam={seznam} kljuc="kupec" naziv="Stranka" />
            <Stolpec seznam={seznam} kljuc="vozilo" naziv="Vozilo" />
            <Stolpec seznam={seznam} kljuc="sezona" naziv="Sezona" />
            <Stolpec seznam={seznam} kljuc="dimenzija" naziv="Gume" />
            <Stolpec seznam={seznam} kljuc="profil" naziv="Profil" />
            <Stolpec seznam={seznam} kljuc="shranjeno" naziv="Shranjeno" />
            <Stolpec seznam={seznam} naziv="Račun" />
          </>
        }
        vrstice={vrstice.map((g, i) => (
          <Table.Tr key={g.id} style={{ cursor: 'pointer' }} onClick={() => void navigate(`/delavnica/gume/${g.id}`)}>
            <Table.Td c="dimmed" className="brez-preloma">
              {seznam.zamik + i + 1}
            </Table.Td>
            <Table.Td>
              <Anchor
                component={Link}
                to={`/delavnica/gume/${g.id}`}
                onClick={(e) => e.stopPropagation()}
                fw={700}
                className="brez-preloma"
              >
                {g.lokacija || '–'}
              </Anchor>
              {g.stanje === 'PREVZET' && (
                <Text size="xs" c="dimmed">
                  {STANJA_GUM.PREVZET} {datum(g.prevzeto)}
                </Text>
              )}
            </Table.Td>
            <Table.Td>
              <Text size="sm">{g.kupec}</Text>
              <Text size="xs" c="dimmed">
                {g.telefon}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm" fw={600} className="brez-preloma">
                {g.registrska}
              </Text>
              <Text size="xs" c="dimmed">
                {g.vozilo}
              </Text>
            </Table.Td>
            <Table.Td>
              {g.sezona && (
                <Badge color={SEZONE[g.sezona].barva} variant="light" className="brez-preloma">
                  {SEZONE[g.sezona].naziv}
                </Badge>
              )}
            </Table.Td>
            <Table.Td>{opisGum(g)}</Table.Td>
            <Table.Td className="brez-preloma">
              <Group gap={4} wrap="nowrap">
                {profil(g.minProfil)}
                {g.obrabljene && (
                  <IconAlertTriangle size={16} color="var(--mantine-color-red-6)" aria-label="Obrabljene" />
                )}
              </Group>
            </Table.Td>
            <Table.Td className="brez-preloma">{datum(g.shranjeno)}</Table.Td>
            <Table.Td className="brez-preloma">
              {g.racunId ? (
                <Anchor component={Link} to={`/racuni/${g.racunId}`} onClick={(e) => e.stopPropagation()} size="sm">
                  {g.stRacuna}
                </Anchor>
              ) : (
                g.zaRacun && (
                  <Badge color="orange" variant="light">
                    Ni izdan
                  </Badge>
                )
              )}
            </Table.Td>
          </Table.Tr>
        ))}
        stVrstic={vrstice.length}
        stStolpcev={8}
        skupaj={poizvedba.data?.skupaj ?? 0}
        nalaganje={poizvedba.isPending}
        osvezevanje={poizvedba.isFetching}
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        prazno="V hotelu ni gum za izbrane pogoje."
        napakaNaslov="Gum ni bilo mogoče naložiti"
      />
    </ZaslonSeznama>
  );
}
